import PropTypes from "prop-types";
import classNames from "classnames";

import { RATING_OPTIONS, RATING_DISPLAY_LABELS, getColorClass } from "../../../../utils/teacher/InputRatingUtils";

/**
 * A small, opinionated rating picker that:
 * - Supports read-only view
 * - Optionally treats empty selection as "UNRATED" (for diff flows)
 * - Shows a "Clear Rating" option that maps to "" or UNRATED depending on allowUnrated
 */
const RatingPicker = ({
    canEdit,
    value,
    onChange,
    allowUnrated = false, // diff = true, content/sle = false
    className,
    selectStyle,
    viewOnlyClassName,
    emptyLabel = "Not Yet Rated"
}) => {
    const getRenderValue = () => {
        if (!allowUnrated) return value;
        return value === "UNRATED" ? "" : value;
    };
    const renderValue = getRenderValue();

    if (!canEdit || value === "EXCLUDED") {
        return (
            <div style={{ marginLeft: 24, minWidth: 220 }}>
                <div
                    role="text"
                    aria-label="Rating"
                    style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 4
                    }}
                >
                    Rating
                </div>
                <div
                    className={classNames("view-only-rating", getColorClass(value), viewOnlyClassName)}
                    style={{ width: "stretch" }}
                >
                    {RATING_DISPLAY_LABELS[value] || value || emptyLabel}
                </div>
            </div>
        );
    }

    const handleChange = (e) => {
        const raw = e.target.value;

        let next;
        if (allowUnrated) {
            next = raw === "" ? "UNRATED" : raw;
        } else {
            next = raw;
        }
        onChange(next);
    };

    return (
        <div style={{ marginLeft: 24, minWidth: 220 }}>
            <label
                htmlFor="rating-select"
                style={{
                    fontSize: 12,
                    display: "block",
                    marginBottom: 4
                }}
            >
                Rating
            </label>
            <select
                id="rating-select"
                aria-label="Rating select"
                className={classNames(getColorClass(value), className)}
                value={renderValue}
                onChange={handleChange}
                style={selectStyle ?? { width: "100%", padding: "6px 8px", borderRadius: 5 }}
            >
                <option value="">— Select —</option>
                {RATING_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                        {RATING_DISPLAY_LABELS[opt] || opt}
                    </option>
                ))}
                {allowUnrated ? <option value="UNRATED">Clear Rating</option> : <option value="">Clear Rating</option>}
            </select>
        </div>
    );
};

RatingPicker.propTypes = {
    canEdit: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    allowUnrated: PropTypes.bool,
    className: PropTypes.string,
    selectStyle: PropTypes.object,
    viewOnlyClassName: PropTypes.string,
    emptyLabel: PropTypes.string
};

RatingPicker.defaultProps = {
    canEdit: true,
    value: "",
    allowUnrated: false,
    className: "",
    selectStyle: null,
    viewOnlyClassName: "",
    emptyLabel: "Not Yet Rated"
};

export default RatingPicker;
